import { FontSizes, FontWeights, LineHeights } from 'apps/webcomponents/src/types/common/types';
import { translateFn } from 'apps/webcomponents/src/utils/translator.helper';
import { LitElement, html, TemplateResult, nothing } from 'lit';
import { property, customElement, state } from 'lit/decorators.js';

import { styles } from '../view/styles';
import { GzTabChangeEvent } from 'apps/webcomponents/src/ui/gzTabs/gzTabGroup/events/GzTabChangeEvent';
import { ComplianceSideMenuDataType, ComplianceSideMenuTabs, RiskType, AssetType } from '../types';
import { ComplianceAssetRow, ComplianceEvents, ComplianceRiskRow } from '../../common/types';
import { getBulletColorForScore } from 'apps/webcomponents/src/riskManagement/common/utils';
import { ChecksResults, EventState } from '@nirvana/shared/dtos/types/compliance-reports/gz-console';
import { formatLargeNumber } from 'apps/webcomponents/src/utils/formatter.utils';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { ImageState } from 'apps/webcomponents/src/ui/gzImageRepo/types';
import { hideIfNoAccessToComplianceStandard } from '../../common/util';
import { AssetsMessageFn, LicenseMessageFn, RiskMessageFn } from '../../common/constant';

/**
 * @summary Compliance side menu web component
 * @tag compliance-side-menu
 * Here https://custom-elements-manifest.open-wc.org/analyzer/getting-started/ you can find out how to document the code
 */
@customElement('compliance-side-menu')
export class ComplianceSideMenuWebComponent extends LitElement {
  @property({ type: Object, attribute: false })
  complianceData: Partial<ComplianceSideMenuDataType> = {};

  @property({ type: String })
  companyId = '';

  @property({ type: Boolean })
  misconfigurationRisksLoading = false;

  @property({ type: Boolean })
  userBehaviorRisksLoading = false;

  @property({ type: Boolean })
  resourceAssetsLoading = false;

  @property({ type: Boolean })
  identityAssetsLoading = false;

  @state()
  tabSelected: ComplianceSideMenuTabs = ComplianceSideMenuTabs.Description;

  @property({ type: Boolean, attribute: false, reflect: false })
  hasAccessToComplianceStandard = false;

  @property({ type: String, attribute: false, reflect: false })
  complianceLearnMoreButton = {
    show: false,
    text: '',
    link: '',
    linkAction: () => {},
  };

  static get styles() {
    return styles;
  }

  render(): TemplateResult {
    // refactor(dhipolito): Styling
    return html`
      <div class="header mt-6">
        <gz-title fontSize="${FontSizes.HEADING_5}" class="mb-2">
          <gz-tooltip
            isEllipsis
            tooltipText="${translateFn('View the full control name in the "Description" tab')}"
            style="width: 100%"
            id="control-name-title"
          >
            <span> ${this.complianceData.complianceStandardLabel} : ${this.complianceData.controlId} </span>
          </gz-tooltip></gz-title
        >
      </div>
      <div class="content" style="scrollbar-gutter: stable">${this.renderGeneralSection()} ${this.renderTabs()}</div>
    `;
  }

  private renderGeneralSection(): TemplateResult {
    const notEvaluated = this.complianceData.totalChecks === this.complianceData.checksBreakdown?.ignored;

    const complianceScoreText = notEvaluated ? 'Not evaluated' : `${this.complianceData.complianceScore}%`;

    return html`
      <div class="section pb-2" data-section="general">
        <gz-title
          fontSize="${FontSizes.PARAGRAPH_11}"
          fontWeight="${FontWeights.SEMIBOLD}"
          class="section-title text-uppercase mb-2"
        >
          ${translateFn('General')}
        </gz-title>
        <gz-labeled-content label="${translateFn('Standard')}">${this.complianceData.complianceStandardLabel}</gz-labeled-content>
        <gz-labeled-content label="${translateFn('Section name')}">
          <div class="d-flex">
          <gz-tooltip tooltipText="${this.complianceData.sectionId}. ${this.complianceData.sectionName}" isEllipsis>
            ${this.complianceData.sectionId}. ${this.complianceData.sectionName}
          </gz-tooltip>
          </div>
        </gz-labeled-content>
        <gz-labeled-content label="${translateFn('Score')}">
          <gz-text fontSize="12" class="text-semibold"> ${hideIfNoAccessToComplianceStandard(this.hasAccessToComplianceStandard, complianceScoreText)} </gz-text>
        </gz-labeled-content>
        <gz-labeled-content label="${translateFn('Checks breakdown')}">
          <div class="d-flex text-semibold">
            ${hideIfNoAccessToComplianceStandard(this.hasAccessToComplianceStandard, this.renderChecksBreakdown(this.complianceData.checksBreakdown as ChecksResults))}
          </div>
        </gz-labeled-content>
      </div>
    `;
  }

  private renderDescriptionTabSection() {
    const controlDescriptionComponent = this.complianceData.controlDescription
      ? html`<gz-text class="text-description" fontSize="${FontSizes.PARAGRAPH_12}" fontWeight="${FontWeights.NORMAL}">
          ${unsafeHTML(this.complianceData.controlDescription)}
        </gz-text>`
      : nothing;

    return html`
      <div class="section py-2" data-section="description">
        <gz-text class="text-description mb-2" fontSize="${FontSizes.PARAGRAPH_12}" fontWeight="${FontWeights.NORMAL}"
          >${this.complianceData.controlId} ${this.complianceData.controlName}</gz-text
        >
        ${controlDescriptionComponent}
      </div>
    `;
  }

  private renderComplianceLearnMoreButton() {
    return html`
      <gz-button @click="${() => this.complianceLearnMoreButton.linkAction()}" id="complianceLearnMoreSideMenuButton">
        ${this.complianceLearnMoreButton.text}
      </gz-button>
    `;
  }

  private renderErrorState(state: ImageState, title: string, description: string) {
    return html`<div class="mb-10 mt-10" data-section="risks">
      <gz-image-repo state="${state}">
        <gz-title
          class="image-repo-title text-gray compliance-content-max-width"
          fontSize="${FontSizes.HEADING_4}"
          fontWeight="500"
          >${title}</gz-title
        >
        <gz-text class="image-repo-text text-gray compliance-content-max-width" fontSize="13" fontWeight="400"
          >${description}</gz-text
        >
        ${this.complianceLearnMoreButton.show ? this.renderComplianceLearnMoreButton() : ''}
      </gz-image-repo>
    </div>`;
  }

  private renderNonPremium(data: TemplateResult) {
    return this.hasAccessToComplianceStandard
      ? data
      : this.renderErrorState(
          ImageState.DENIED,
          LicenseMessageFn.LICENSE_RESTRICTED_TITLE(),
          LicenseMessageFn.LICENSE_RESTRICTED_MESSAGE()
        );
  }

  private renderRisksTabSection() {
    if (
      (!this.complianceData.misconfigurationRisksTotalCount ||
        this.complianceData.misconfigurationRisksTotalCount === 0) &&
      (!this.complianceData.userBehaviorRisksTotalCount || this.complianceData.userBehaviorRisksTotalCount === 0)
    ) {
      this.renderErrorState(
        ImageState.EMPTY,
        RiskMessageFn.RISK_NOT_FOUND_TITLE(),
        RiskMessageFn.RISK_NOT_FOUND_MESSAGE()
      );
    }

    return html`<div class="section py-2" data-section="risks">
      <div class="">${this.renderMisconfigurationRisksSection()}</div>
      <div>${this.renderUserBehaviorRisksSection()}</div>
    </div>`;
  }

  private renderAssetsTabSection() {
    if (
      (!this.complianceData.misconfigurationRisksTotalCount ||
        this.complianceData.misconfigurationRisksTotalCount === 0) &&
      (!this.complianceData.userBehaviorRisksTotalCount || this.complianceData.userBehaviorRisksTotalCount === 0) &&
      (!this.complianceData.resourceAssetsTotalCount || this.complianceData.resourceAssetsTotalCount === 0) &&
      (!this.complianceData.identityAssetsTotalCount || this.complianceData.identityAssetsTotalCount === 0)
    ) {
      this.renderErrorState(
        ImageState.EMPTY,
        AssetsMessageFn.ASSETS_NOT_FOUND_TITLE(),
        AssetsMessageFn.ASSETS_NOT_FOUND_MESSAGE()
      );
    }

    return html`<div class="section py-2" data-section="assets">
      ${this.renderResourceAssetsSection()} ${this.renderIdentityAssetsSection()}
    </div>`;
  }

  private renderTabs(): TemplateResult {
    return html`
      <gz-tab-group @gzTabChange="${this.handleTabChange}">
        <gz-tab
          id="description-tab"
          label="${translateFn('Description')}"
          .selected="${this.tabSelected === ComplianceSideMenuTabs.Description}"
        >
          ${this.renderDescriptionTabSection()}
        </gz-tab>
        <gz-tab
          id="risks-tab"
          label="${translateFn('Risks')}"
          .selected="${this.tabSelected === ComplianceSideMenuTabs.Risks}"
        >
          ${this.renderNonPremium(this.renderRisksTabSection())}
        </gz-tab>
        <gz-tab
          id="assets-tab"
          label="${translateFn('Affected assets')}"
          .selected="${this.tabSelected === ComplianceSideMenuTabs.Assets}"
        >
          ${this.renderNonPremium(this.renderAssetsTabSection())}
        </gz-tab>
      </gz-tab-group>
    `;
  }

  private renderMisconfigurationRisksSection() {
    const misconfigurationsTotalCount = this.complianceData.misconfigurationRisksTotalCount;
    const misconfigurationsReturnedCount = this.complianceData.misconfigurationRisks?.length ?? 0;
    const onClickDetails = {
      type: 'misconfiguration' as RiskType,
      indicatorIds: this.complianceData.misconfigurationRisksIndicatorIds ?? [],
    };

    const rowElements = this.misconfigurationRisksLoading
      ? html`<gz-loader
          rows="${misconfigurationsTotalCount && misconfigurationsTotalCount < 5 ? misconfigurationsTotalCount : 5}"
        ></gz-loader>`
      : this.complianceData.misconfigurationRisks && this.complianceData.misconfigurationRisks.length > 0
      ? this.complianceData.misconfigurationRisks.map(row => this.renderRiskRow(row, 'misconfiguration'))
      : this.renderEmptyRow(translateFn('No risks found'));

    const moreItems =
      !this.misconfigurationRisksLoading && misconfigurationsTotalCount
        ? this.renderMoreItems(misconfigurationsTotalCount, misconfigurationsReturnedCount, onClickDetails)
        : nothing;

    return html`<div class="d-flex align-items-center mb-1">
        <gz-text
          fontSize="${FontSizes.PARAGRAPH_11}"
          fontWeight="${FontWeights.MEDIUM}"
          class="section-title text-uppercase"
          >${translateFn('Findings')} ${`(${misconfigurationsTotalCount ?? 0})`}
        </gz-text>
      </div>
      <div class="mb-1">${rowElements}</div>
      ${moreItems}`;
  }

  private renderUserBehaviorRisksSection() {
    const risksTotalCount = this.complianceData.userBehaviorRisksTotalCount;
    const riskReturnedCount = this.complianceData.userBehaviorRisks?.length ?? 0;
    const onClickDetails = {
      type: 'user-behavior-risk' as RiskType,
      indicatorIds: this.complianceData.userBehaviorRiskIndicatorIds ?? [],
    };

    const moreItems =
      !this.userBehaviorRisksLoading && risksTotalCount
        ? this.renderMoreItems(risksTotalCount, riskReturnedCount, onClickDetails)
        : nothing;

    const rowElements = this.userBehaviorRisksLoading
      ? html`<gz-loader rows="${risksTotalCount && risksTotalCount < 5 ? risksTotalCount : 5}"></gz-loader>`
      : this.complianceData.userBehaviorRisks && this.complianceData.userBehaviorRisks.length > 0
      ? this.complianceData.userBehaviorRisks.map(row => this.renderRiskRow(row, 'user-behavior-risk'))
      : this.renderEmptyRow(translateFn('No risks found'));

    return html`<div class="d-flex align-items-center mb-1">
        <gz-text
          fontSize="${FontSizes.PARAGRAPH_11}"
          fontWeight="${FontWeights.MEDIUM}"
          class="section-title text-uppercase"
          >${translateFn('User behavior risks')} ${`(${this.complianceData.userBehaviorRisksTotalCount ?? 0})`}
        </gz-text>
      </div>
      <div class="mb-1">${rowElements}</div>
      ${moreItems}`;
  }

  private renderResourceAssetsSection() {
    const resourcesTotalCount = this.complianceData.resourceAssetsTotalCount;
    const resourcesReturnedCount = this.complianceData.resourceAssets?.length ?? 0;
    const onClickDetails = {
      type: 'resource-asset' as AssetType,
      indicatorIds: this.complianceData.misconfigurationRisksIndicatorIds ?? [],
    };

    const moreItems =
      !this.resourceAssetsLoading && resourcesTotalCount
        ? this.renderMoreItems(resourcesTotalCount, resourcesReturnedCount, onClickDetails)
        : nothing;

    const rowElements = this.resourceAssetsLoading
      ? html`<gz-loader rows="5"></gz-loader>`
      : this.complianceData.resourceAssets && this.complianceData.resourceAssets.length > 0
      ? this.complianceData.resourceAssets.map(row => this.renderAssetRow(row, 'resource-asset'))
      : this.renderEmptyRow(translateFn('No assets found'));

    return html`<div class="d-flex align-items-center mb-1">
        <gz-text
          fontSize="${FontSizes.PARAGRAPH_11}"
          fontWeight="${FontWeights.MEDIUM}"
          class="section-title text-uppercase"
          >${translateFn('Resources')} ${`(${this.complianceData.resourceAssetsTotalCount ?? 0})`}
        </gz-text>
      </div>
      <div class="mb-1">${rowElements}</div>
      ${moreItems}`;
  }

  private renderIdentityAssetsSection() {
    const identitiesTotalCount = this.complianceData.identityAssetsTotalCount;
    const identitiesReturnedCount = this.complianceData.identityAssets?.length ?? 0;
    const onClickDetails = {
      type: 'identity-asset' as AssetType,
      indicatorIds: this.complianceData.userBehaviorRiskIndicatorIds ?? [],
    };

    const moreItems =
      !this.identityAssetsLoading && identitiesTotalCount
        ? this.renderMoreItems(identitiesTotalCount, identitiesReturnedCount, onClickDetails)
        : nothing;

    const rowElements = this.identityAssetsLoading
      ? html`<gz-loader rows="5"></gz-loader>`
      : this.complianceData.identityAssets && this.complianceData.identityAssets.length > 0
      ? this.complianceData.identityAssets.map(row => this.renderAssetRow(row, 'identity-asset'))
      : this.renderEmptyRow(translateFn('No assets found'));

    return html`<div class="d-flex align-items-center mb-1">
        <gz-text
          fontSize="${FontSizes.PARAGRAPH_11}"
          fontWeight="${FontWeights.MEDIUM}"
          class="section-title text-uppercase"
          >${translateFn('Identities')} ${`(${this.complianceData.identityAssetsTotalCount ?? 0})`}
        </gz-text>
      </div>
      <div class="mb-1">${rowElements}</div>
      ${moreItems}`;
  }

  // refactor(dhipolito): Remove redundancies
  private renderRiskRow(row: ComplianceRiskRow, type: RiskType): TemplateResult {
    // TODO: Add refactor these styling to css after release
    const checkResultWidth = 176;
    const rowNameWidthOffset = checkResultWidth + 30;

    return html`
      <gz-accordion align="left" nonCollapsable doNotShowIcon>
        <div
          slot="dynamic-title-content"
          class="cell-label min-width-zero d-flex flex-1 align-items-center accordion-title pointer"
          @click="${() => row.indicatorId && this.handleRowClick(type, [row.indicatorId])}"
        >
          <div class="d-flex min-width-zero flex-1 justify-space-between align-items-center">
            <gz-tooltip isEllipsis tooltipText="${row.name}" style="max-width: calc(100% - ${rowNameWidthOffset}px">
              <gz-bullet color=${getBulletColorForScore(row.riskScore)}>
                <gz-text class="d-inline" fontSize="${FontSizes.PARAGRAPH_12}">${row.name}</gz-text>
              </gz-bullet>
            </gz-tooltip>
            <div class="d-flex justify-end" style="width:${checkResultWidth}px;">
              ${this.renderChecksResult(row.checksStats.checksResults, row.state, type)}
            </div>
          </div>
        </div>
      </gz-accordion>
    `;
  }

  private renderEmptyRow(message: string): TemplateResult {
    return html`
      <gz-accordion align="left" nonCollapsable doNotShowIcon>
        <div
          slot="dynamic-title-content"
          class="cell-label min-width-zero d-flex flex-1 align-items-center accordion-title"
        >
          <div class="d-flex min-width-zero flex-1 justify-space-between align-items-center">
            <gz-text class="d-inline text-gray" fontSize="${FontSizes.PARAGRAPH_12}">${message}</gz-text>
          </div>
        </div>
      </gz-accordion>
    `;
  }

  private renderChecksResult(checksResults: ChecksResults, state: EventState, type: RiskType | AssetType) {
    const { ignored, nonCompliant, compliant } = checksResults;

    let scoreTemplate = html``;
    let padding = 0;

    if (compliant > 0) {
      scoreTemplate = html`${scoreTemplate}
        <div class="d-flex align-items-center flex-1">
          <gz-tooltip tooltipText="${translateFn('Compliant checks')}">
            <gz-icon
              color="var(--green-layout-100-primary-green)"
              size="20"
              name="ds-icon-notification_success"
              class="check-result"
            ></gz-icon>
          </gz-tooltip>
          <gz-text fontSize="13">${formatLargeNumber(compliant)}</gz-text>
        </div> `;
    } else {
      padding += 1;
    }

    if (nonCompliant > 0) {
      scoreTemplate = html`${scoreTemplate}
        <div class="d-flex align-items-center flex-1">
          <gz-tooltip tooltipText="${translateFn('Non-compliant checks')}">
            <gz-icon color="var(--red-layout-300-primary)" size="20" name="ds-icon-delete_large" class="check-result"></gz-icon>
          </gz-tooltip>
          <gz-text fontSize="13">${formatLargeNumber(nonCompliant)}</gz-text>
        </div> `;
    } else {
      padding += 1;
    }

    const ignoredSection = html`
      <div class="d-flex align-items-center flex-1">
        ${this.getIgnoredIcon(20)}
        <gz-text fontSize="13">${formatLargeNumber(ignored)}</gz-text>
      </div>
    `;

    if (ignored > 0) {
      scoreTemplate = html` ${scoreTemplate} ${ignoredSection} `;
    } else {
      padding += 1;
    }

    if (padding > 0) {
      scoreTemplate = html`<div class="flex-${padding}"></div>
        ${scoreTemplate}`;
    }

    if (state === EventState.IGNORED) {
      let ignoredText = 'Ignored risk';

      if (type === 'resource-asset' || type === 'identity-asset') {
        ignoredText = 'Ignored asset';
      }

      scoreTemplate = html`
        <gz-text fontSize="13" class="text-center text-gray flex-2">${ignoredText}</gz-text>
        ${ignoredSection}
      `;
    }

    return scoreTemplate;
  }

  private renderChecksBreakdown(checksResults: ChecksResults) {
    const { ignored, nonCompliant, compliant } = checksResults;

    let scoreTemplate = html`
      <div class="d-flex align-items-center mr-1">
        <gz-icon
          color="var(--green-layout-100-primary-green)"
          size="20"
          name="ds-icon-notification_success"
          class="check-result"
        ></gz-icon>
        <gz-text fontSize="12">${formatLargeNumber(compliant)} Compliant</gz-text>
      </div>
      <div class="d-flex align-items-center mr-1">
        <gz-icon color="var(--red-chart-600)" size="20" name="ds-icon-delete_large" class="check-result"></gz-icon>
        <gz-text fontSize="12">${formatLargeNumber(nonCompliant)} Non-compliant</gz-text>
      </div>
    `;

    return html`
      ${scoreTemplate}
      <div class="d-flex align-items-center mr-1">
        ${this.getIgnoredIcon(20)}
        <gz-text fontSize="12">${formatLargeNumber(ignored)} Ignored</gz-text>
      </div>
    `;
  }

  // TODO(dhipolito): Fix
  private renderAssetRow(row: ComplianceAssetRow, type: AssetType): TemplateResult {
    // TODO: Add refactor these styling to css after release
    const checkResultWidth = 176;
    const rowNameWidthOffset = checkResultWidth + 30;

    return html`
      <gz-accordion align="left" nonCollapsable doNotShowIcon>
        <div
          slot="dynamic-title-content"
          class="cell-label min-width-zero d-flex flex-1 align-items-center accordion-title pointer"
          @click="${() => row.id && this.handleRowClick(type, [], [{ label: row.name, value: row.id }])}"
        >
          <div class="d-flex min-width-zero flex-1 justify-space-between align-items-center">
            <gz-tooltip isEllipsis tooltipText="${row.name}" style="max-width: calc(100% - ${rowNameWidthOffset}px">
              <gz-bullet color=${getBulletColorForScore(row.riskScore)}>
                <gz-text class="d-inline" fontSize="${FontSizes.PARAGRAPH_12}">${row.name}</gz-text>
              </gz-bullet>
            </gz-tooltip>
            <div class="d-flex justify-end" style="width:${checkResultWidth}px;">
              ${this.renderChecksResult(row.checksStats.checksResults, row.state, type)}
            </div>
          </div>
          </div>
        </div>
      </gz-accordion>
    `;
  }

  private renderMoreItems(
    totalAvailableItemCount: number,
    totalReturnedItemCount: number,
    onClickDetails: { type: RiskType | AssetType; indicatorIds: number[] }
  ) {
    const viewAllButton =
      totalAvailableItemCount > 0
        ? html`<gz-button
            @click="${() =>
              // `View all` includes all control Indicator Ids (not just indicators shown in the drawer)
              this.handleRowClick(onClickDetails.type, onClickDetails.indicatorIds)}"
            type="quiet"
            color="primary"
            theme="light"
            buttonheight="small"
            buttonfontsize="small"
            >${translateFn('View all')}
            ${onClickDetails.type === 'resource-asset'
              ? translateFn('Resources')
              : onClickDetails.type === 'identity-asset'
              ? translateFn('Identities')
              : onClickDetails.type === 'misconfiguration'
              ? translateFn('Findings')
              : translateFn('Risks')}</gz-button
          >`
        : nothing;

    const moreItems =
      totalAvailableItemCount > totalReturnedItemCount
        ? html`<gz-text
            class="d-inline text-gray"
            fontSize="${FontSizes.PARAGRAPH_12}"
            lineHeight="${LineHeights.SMALL}"
            >${translateFn(`and {numberLeft} more`, {
              numberLeft: formatLargeNumber(totalAvailableItemCount - totalReturnedItemCount),
            })}
          </gz-text>`
        : nothing;

    return html`<div class="align-items-start mb-3">${moreItems} ${viewAllButton}</div> `;
  }

  private getIgnoredIcon(size: number): TemplateResult {
    return html`
      <gz-tooltip tooltipText="${translateFn('Ignored checks')}">
        <gz-icon size="${size}" class="check-result">
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            slot="custom-svg"
          >
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M10.005 15.8399C13.2276 15.8399 15.84 13.2275 15.84 10.0049C15.84 6.78234 13.2276 4.16992 10.005 4.16992C6.78246 4.16992 4.17004 6.78234 4.17004 10.0049C4.17004 13.2275 6.78246 15.8399 10.005 15.8399ZM10.005 18.3399C14.6083 18.3399 18.34 14.6082 18.34 10.0049C18.34 5.40163 14.6083 1.66992 10.005 1.66992C5.40175 1.66992 1.67004 5.40163 1.67004 10.0049C1.67004 14.6082 5.40175 18.3399 10.005 18.3399Z"
              fill="var(--chart-105-solid)"
            ></path>
          </svg>
        </gz-icon>
      </gz-tooltip>
    `;
  }

  private handleTabChange(event: GzTabChangeEvent) {
    this.tabSelected = event.currentTab;

    if (this.tabSelected === ComplianceSideMenuTabs.Risks) {
      this.dispatchEvent(
        new CustomEvent(ComplianceEvents.SIDE_MENU_RISKS_DETAILS, {
          detail: {
            component: this,
            companyId: this.companyId,
            complianceStandard: this.complianceData.complianceStandard,
            indicatorIds: {
              misconfiguration: this.complianceData.misconfigurationRisksIndicatorIds,
              userBehaviorRisk: this.complianceData.userBehaviorRiskIndicatorIds,
            },
          },
          bubbles: true,
          cancelable: true,
          composed: true,
        })
      );
    }

    if (this.tabSelected === ComplianceSideMenuTabs.Assets) {
      this.dispatchEvent(
        new CustomEvent(ComplianceEvents.SIDE_MENU_ASSETS_DETAILS, {
          detail: {
            component: this,
            companyId: this.companyId,
            complianceStandard: this.complianceData.complianceStandard,
            indicatorIds: {
              misconfiguration: this.complianceData.misconfigurationRisksIndicatorIds,
              userBehaviorRisk: this.complianceData.userBehaviorRiskIndicatorIds,
            },
          },
          bubbles: true,
          cancelable: true,
          composed: true,
        })
      );
    }
  }

  private handleRowClick(
    type: RiskType | AssetType,
    indicatorIds: number[],
    assetIds?: {
      label?: string;
      value?: string;
    }[]
  ) {
    const complianceStandard = this.complianceData.complianceStandard;

    if (type === 'misconfiguration') {
      this.dispatchEvent(
        new CustomEvent(ComplianceEvents.VIEW_MISCONFIGURATION_DETAILS, {
          detail: {
            companyId: this.companyId,
            indicatorIds: indicatorIds,
            complianceStandard,
          },
          bubbles: true,
          cancelable: true,
          composed: true,
        })
      );
    } else if (type === 'user-behavior-risk') {
      this.dispatchEvent(
        new CustomEvent(ComplianceEvents.VIEW_HUMAN_RISKS_BY_RISKS, {
          detail: {
            companyId: this.companyId,
            indicatorIds: indicatorIds,
            complianceStandard,
          },
          bubbles: true,
          cancelable: true,
          composed: true,
        })
      );
    } else if (type === 'resource-asset') {
      this.dispatchEvent(
        new CustomEvent(ComplianceEvents.VIEW_RESOURCES_BY_MISCONFIGURATIONS, {
          detail: {
            companyId: this.companyId,
            indicatorIds: indicatorIds,
            assetIds: assetIds,
            operatingSystem: this.complianceData.controlExtraDetails?.controlOperatingSystem,
          },
          bubbles: true,
          cancelable: true,
          composed: true,
        })
      );
    } else if (type === 'identity-asset') {
      this.dispatchEvent(
        new CustomEvent(ComplianceEvents.VIEW_IDENTITIES_BY_RISKS, {
          detail: {
            companyId: this.companyId,
            assetIds: assetIds,
            applicableResource: this.complianceData.controlExtraDetails?.controlApplicableResource,
          },
          bubbles: true,
          cancelable: true,
          composed: true,
        })
      );
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'compliance-side-menu': ComplianceSideMenuWebComponent;
  }
}
